<template>
  <div class="notification-cards" aria-live="polite">
    <TransitionGroup name="notification-card" tag="div" class="notification-cards__list">
      <v-card
        v-for="card in store.cards"
        :key="card.id"
        class="notification-card"
        elevation="8"
        rounded="lg"
        @click.stop
      >
        <div class="notification-card__inner">
          <v-btn
            icon
            variant="text"
            size="x-small"
            class="notification-card__close"
            :aria-label="t('actions.close')"
            @click="store.remove(card.id)"
          >
            <v-icon icon="mdi-close" size="18" />
          </v-btn>
          <!-- Con póster se ve de un vistazo qué se ha añadido; si no, el icono de siempre. -->
          <div v-if="card.image && !failedImages.has(card.id)" class="notification-card__poster">
            <img :src="card.image" :alt="card.title" loading="lazy" @error="failedImages.add(card.id)" />
          </div>
          <div v-else class="notification-card__icon-wrap">
            <v-icon :icon="card.icon" size="24" class="notification-card__icon" />
          </div>
          <div class="notification-card__body">
            <p class="notification-card__title">{{ card.title }}</p>
            <p v-if="card.message" class="notification-card__message">{{ card.message }}</p>
            <div v-if="card.actionRoute" class="notification-card__actions">
              <v-btn
                :to="card.actionRoute"
                variant="flat"
                size="small"
                class="notification-card__btn notification-card__btn--ver"
                @click="store.remove(card.id)"
              >
                {{ card.actionText }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-card>
    </TransitionGroup>
  </div>
</template>

<script setup>
import { reactive } from 'vue'
import { useNotificationCardsStore } from '@/stores/notificationCards'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const store = useNotificationCardsStore()

// Pósters que no cargan (URL de TMDB caída, imagen borrada...): se cae al icono.
const failedImages = reactive(new Set())
</script>

<style scoped>
.notification-cards {
  position: fixed;
  top: 64px;
  right: 16px;
  z-index: 1000;
  pointer-events: none;
}

.notification-cards__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-end;
  min-width: 0;
  pointer-events: none;
}

.notification-card {
  pointer-events: auto;
  max-width: 360px;
  min-width: 280px;
  overflow: hidden;
}

.notification-card__inner {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 40px 14px 16px;
}

.notification-card__close {
  position: absolute;
  top: 8px;
  right: 8px;
  color: rgba(255, 255, 255, 0.7);
}

.notification-card__close:hover {
  color: rgba(255, 255, 255, 0.95);
}

.notification-card__icon-wrap {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8e2de2 0%, #ff007f 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-card__icon {
  color: #fff;
}

.notification-card__poster {
  flex-shrink: 0;
  width: 44px;
  height: 64px;
  border-radius: var(--ws-radius-sm);
  overflow: hidden;
  background: var(--ws-glass-2);
}

.notification-card__poster img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.notification-card__body {
  flex: 1;
  min-width: 0;
}

.notification-card__title {
  font-weight: 600;
  font-size: 0.9375rem;
  line-height: 1.3;
  margin: 0 0 4px 0;
  color: rgb(var(--v-theme-on-surface));
}

.notification-card__message {
  font-size: 0.8125rem;
  line-height: 1.4;
  color: rgba(255, 255, 255, 0.72);
  margin: 0 0 10px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-card__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.notification-card__btn {
  text-transform: none;
  font-weight: 600;
  min-width: 0;
}

.notification-card__btn--ver {
  background: linear-gradient(135deg, #8e2de2 0%, #ff007f 100%) !important;
  color: #fff !important;
  border-radius: 9999px;
}

.notification-card__btn--ver:hover {
  opacity: 0.92;
}

/* Entrada/salida */
.notification-card-enter-active {
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.25s ease-out;
}

.notification-card-leave-active {
  transition: transform 0.25s ease-in, opacity 0.2s ease-in;
}

.notification-card-enter-from {
  transform: translateX(100%);
  opacity: 0;
}

.notification-card-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.notification-card-move {
  transition: transform 0.3s ease;
}
</style>
